import { useEffect, useRef, useState } from 'react';
import { track, useContent } from '../../logic';
import { PortalNotificationReference } from '../../types';
import { useLocation, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Typography, useMediaQuery } from '@mui/material';
import { TimelineItem, TimelineSeparator, Timeline, TimelineConnector, TimelineDot, TimelineContent } from '@mui/lab';
import SendIcon from '@mui/icons-material/Send';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CloseIcon from '@mui/icons-material/Close';
import { getNotificationsInfo } from '../../utils';
import { ChevronRight } from '@mui/icons-material';
import { LinkWithSearchParams } from './LinkWithSearchParams';
import { useOptimisticUpdateOrderNotifications } from '../../logic/optimisticUpdates';
import { useGetNotifications, useGetOrder } from '../../logic/useApiQuery';
import { useAppTheme } from '../../logic/useAppTheme';

const notificationsPollingDelay = 8 * 1000;
const defaultNotificationsPollMax = 5


export const usePollNotifications = () => {
  const [isPolling, setIsPolling] = useState(false)
  const [pollCount, setPollCount] = useState(0);
  const [lastPoll, setLastPoll] = useState(0)
  const notificationsCountAtPollStart = useRef<number>(0)
  const pollCountRef = useRef<number>(0)
  const { closingId } = useParams();
  const { search } = useLocation();
  const { optimisticUpdateOrderNotifications } = useOptimisticUpdateOrderNotifications();
  const { data: notifications, refetch, error, isFetching } = useGetNotifications(closingId ?? '', search, {
    enabled: isPolling,
    onSuccess: (data) => {
      if (!data) return
      optimisticUpdateOrderNotifications({orderKey: closingId || '', search, patch: data })

      if (pollCountRef.current > 0 && data.length > notificationsCountAtPollStart.current) {
        resetPolling()
      }
    }
  });

  function startPolling(initialCount: number) {
    notificationsCountAtPollStart.current = initialCount
    setIsPolling(true)
  }

  function resetPolling() {
    setIsPolling(false)
    setPollCount(0)
    pollCountRef.current = 0
    setLastPoll(0)
  }

  function pollNotifications() {
    const now = Date.now()
    const elapsed = now - lastPoll
    const remainingDelay = notificationsPollingDelay - elapsed
    const pollingMaxReached = pollCount >= defaultNotificationsPollMax

    if (pollingMaxReached) {
      resetPolling()
    } else {
      setTimeout(() => {
        refetch()
        setLastPoll(Date.now())
        setPollCount((prevCount) => {
          pollCountRef.current = prevCount + 1
          return prevCount + 1
        })
      }, Math.max(remainingDelay, 0))
    }
  }


  useEffect(() => {
    if (isPolling && !isFetching) {
      pollNotifications()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetching, isPolling]);

  if (error) {
    track('error', 'useGetNotifications', {}, {
      message: JSON.stringify(error),
      component: 'usePollNotifications',
    });
  }

  return { isPolling, startPolling, pollCount, resetPolling, notifications }
};


export const useNotificationsDialog = () => {
  const { closingId, id } = useParams();
  const { search } = useLocation();

  const { isPolling, startPolling, pollCount, resetPolling, notifications } = usePollNotifications();
  const [open, setOpen] = useState(false);
  const theme = useAppTheme() 
  const primaryColor = theme.palette.primary.main
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const uiContent = useContent()
  const { TaskText, TasksText } = uiContent
  const [eventCount, setEventCount] = useState(0)
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search)

  // IMPROVE: there's some dupe code here with NotificationPagination. Could be pulled into a hook.
  const notificationsRefs = notifications || globalOrderData?.Notifications || []
  const notificationsInfo = getNotificationsInfo(notificationsRefs, uiContent, globalOrderData!)
  const incompleteTasks: PortalNotificationReference[] = notificationsInfo.incompleteTasks.filter((task) => task.NotificationId !== id)
  const hasIncompleteTask = incompleteTasks.length > 0
  const mostRecentIncompleteTask = incompleteTasks[0]

  const timelineEvents = [
    {
      title: 'Sending...',
      description: `Your ${TaskText} details are being sent to our system`,
      icon: <SendIcon />,
    },
    {
      title: 'Processing...',
      description: `We are checking your ${TaskText} details`,
      icon: <PlaylistAddCheckIcon />,

      // why doesn't this one work?
      // icon: <ChecklistRtlIcon />,
    },
    {
      title: 'Still processing...',
      description: 'Thank you for your patience!',
      icon: <PlaylistAddCheckIcon />,
    },
    {
      title: 'Searching...',
      description: `Checking for new ${TasksText}`,
      icon: <ManageSearchIcon />,
    },
    {
      title: `${hasIncompleteTask ? 'Sending...' : 'Finishing up...'}`,
      description: `${hasIncompleteTask ? `Another ${TaskText} is on its way` : `Your ${TasksText} are complete!`}`,
      icon: <AssignmentReturnIcon />,
    },
  ];

  // Spread the addition of timelineEvents evenly across the total polling time.
  // Subtract one to account for wanting the last polling event to return before 
  // the last event is added to the UI
  const addEventInterval = Math.max(
    notificationsPollingDelay * defaultNotificationsPollMax / Math.max(timelineEvents.length - 1, 1),
    2000 // Minimum interval
  )

  const handleClose = () => {
    setOpen(false);
    resetPolling();
  };

  const handleOpen = () => {
    setOpen(true);
    startPolling(notificationsRefs.length);
  }

  useEffect(() => {
    if (open) {
      const timer = setInterval(() => {
        setEventCount((prev) => {
          if (prev < timelineEvents.length) {
            return prev + 1
          } else {
            clearInterval(timer)
            return prev
          }
        })
      }, addEventInterval)
      return () => clearInterval(timer)
    } else {
      setEventCount(0)
    }
  }, [open, timelineEvents.length, addEventInterval]);

  // Jump timeline to completion when polling stops (e.g., new notification found)
  useEffect(() => {
    if (open && !isPolling && pollCount > 0) {
      setEventCount(timelineEvents.length)
    }
  }, [open, isPolling, pollCount, timelineEvents.length]);


  const showActionButton = eventCount === timelineEvents.length

  return {
    pollNotificationsDialog: (
      <>
        {/* TODO: adjust dialog width smaller on big screens. Also make the TimelineConnectors longer on tall screens  */}
        <Dialog
          open={open}
          onClose={handleClose}
          fullScreen={isSmall ? true : false}
          disableEscapeKeyDown={true}
          sx={{
            height: '100%',
            padding: { xs: '8px' },
            '& .MuiDialog-paper': { borderRadius: '20px', height: '100%' }
          }}
          fullWidth={true}
          maxWidth='xl'
        >
          <DialogTitle sx={{
            'borderBottom': `1px solid ${primaryColor}`,
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            Processing Your {TaskText}
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 10,
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Timeline position="alternate" sx={{ pt: 2 }}>
              {timelineEvents.map((event, index) => {
                if (index > eventCount ) {
                  return null;
                } else {
                  return (
                    <TimelineItem key={index}>
                      <TimelineSeparator>
                        <TimelineConnector />
                        <TimelineDot>
                          {index === eventCount ? <CircularProgress sx={{ color: 'white' }} size={24}/> : event.icon}
                        </TimelineDot>
                        <TimelineConnector />
                      </TimelineSeparator>
                      <TimelineContent sx={{ py: '12px', px: 2 }}>
                        <Typography sx={{ fontWeight: 'bold' }} component="span">
                          {event.title}
                        </Typography>
                        <Typography variant="body2">{event.description}</Typography>
                      </TimelineContent>
                    </TimelineItem>
                  )
                }
              })}
            </Timeline>
            {showActionButton && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {hasIncompleteTask ? (
                  <Button
                    fullWidth={true}
                    variant="contained"
                    startIcon={<Box sx={{flex: 1, ml: 2}}></Box>}
                    endIcon={<ChevronRight/>}
                    component={LinkWithSearchParams}
                    to={`/closings/${closingId}/notifications/${mostRecentIncompleteTask?.NotificationId}`}
                    sx={{
                      mt: 1, maxWidth: '300px', justifyContent: 'space-between',
                      '&:hover': { backgroundColor: 'primary.dark', color: 'white' },
                    }}
                  >
                    Go to your next {TaskText}
                  </Button>
                ) : (
                  <Button
                    onClick={handleClose}
                    fullWidth={true}
                    variant="contained"
                    startIcon={<Box sx={{flex: 1, ml: 2}}></Box>}
                    endIcon={<ChevronRight/>}
                    sx={{
                      mt: 1, maxWidth: '300px', justifyContent: 'space-between',
                      '&:hover': { backgroundColor: 'primary.dark', color: 'white' },
                    }}
                  >
                    Close
                  </Button>
                )}
              </Box>
            )}
          </DialogContent>
        </Dialog>
      </>
    ),
    handleOpen
  };
};
